import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getDailyAvailableCombinations, bookCombination } from "@/api/planner";
import ScheduleCombinationCard, {
  Combination,
} from "./CombinationCard";
import { buildSearch } from "@/utils/query";
import { COMBINATION, GA_EVENT, trackEvent } from "@/utils/gaUtil";

type Props = {
  date: string;
  selectedShopIds: number[];
  fixedShopIds: number[];
  tourSize: string;
  customerName?: string;
  customerPhone?: string;
  onCountChange?: (count: number) => void;
};

export default function DailyCombinationSection({
  date,
  selectedShopIds,
  tourSize,
  fixedShopIds,
  customerName,
  customerPhone,
  onCountChange,
}: Props) {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [bookingIndex, setBookingIndex] = useState<number | null>(null);
  const [combinations, setCombinations] = useState<Combination[]>([]);

  useEffect(() => {
    let mounted = true;

    const fetchCombinations = async () => {
      if (!date || selectedShopIds.length === 0) {
        if (!mounted) return;
        setCombinations([]);
        onCountChange?.(0);
        return;
      }

      setLoading(true);

      try {
        const result = await getDailyAvailableCombinations({
          selectedShopIds,
          tourSize,
          date,
          fixedShopIds,
        });
        if (!mounted) return;

        const nextCombinations = result?.combinations || [];
        setCombinations(nextCombinations);
        onCountChange?.(nextCombinations.length);
      } catch (error) {
        if (!mounted) return;
        setCombinations([]);
        onCountChange?.(0);
        console.error("getDailyAvailableCombinations error", error);
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    fetchCombinations();

    return () => {
      mounted = false;
    };
  }, [date, selectedShopIds.join(","), tourSize]);

  const handleBook = async (combinationIndex: number) => {
    const selectedCombination = combinations[combinationIndex];

    if (!selectedCombination || selectedCombination.slots.length === 0) {
      return;
    }

    try {
      setBookingIndex(combinationIndex);
      try {
        trackEvent(GA_EVENT.COMBINATION_SELECT, {
          slotIds: selectedCombination.slots.map((v) => v.id).join(","),
          selectedDate: date,
          tourSize,
          selectedShopIds: selectedShopIds.join(","),
          bookingPath: COMBINATION
        })
      } catch (e) {
        
      }
      navigate({
        pathname: "/confirmation",
        search: buildSearch({
          slotIds: selectedCombination.slots.map((v) => v.id).join(","),
          selectedDate: date,
          tourSize,
          selectedShopIds: selectedShopIds.join(","),
          bookingPath: COMBINATION
        }),
      })
        
      return 
      // const result = await bookCombination({
      //   slots: selectedCombination.slots.map((v) => v.id),

      // });

      // const reservationCode = result?.data?.reservationCode;

      // if (!reservationCode) {
      //   throw new Error("reservationCode가 없습니다.");
      // }

      // navigate({
      //   pathname: `/reservations/${reservationCode}`,
      //   search: `confirm=true`,
      // });
    } catch (error) {
      console.error("bookCombination error", error);
    } finally {
      setBookingIndex(null);
    }
  };
  const handleMoveToBuilder = (combinationIndex: number) => {
    trackEvent(GA_EVENT.COMBINATION_TO_SLOT_VIEW)
    function stringifySelectedSlotsFromCombination(
      slots: Array<{ id: number; shopId: number }>
    ) {
      return slots.map((slot) => `${slot.shopId}:${slot.id}`).join("|");
    }

    function getWeekStartString(dateString: string) {
      const d = new Date(dateString);
      const day = d.getDay(); // 0:일 ~ 6:토
      const diff = day === 0 ? -6 : 1 - day; // 월요일 시작 기준
      d.setDate(d.getDate() + diff);

      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const date = String(d.getDate()).padStart(2, "0");
      return `${year}-${month}-${date}`;
    }
    const selectedCombination = combinations[combinationIndex];

    if (!selectedCombination || selectedCombination.slots.length === 0) {
      return;
    }

    const slotIds = selectedCombination.slots.map((v) => v.id);
    const selectedSlots = stringifySelectedSlotsFromCombination(
      selectedCombination.slots
    );

    const combinationShopIds = Array.from(
      new Set(selectedCombination.slots.map((v) => v.shopId))
    );

    navigate({
      pathname: "/shop-calendar",
      search: buildSearch({
        selectedShopIds: combinationShopIds.join(","),
        selectedSlots,
        selectedDate: date,
        weekStart: getWeekStartString(date),
        slotIds: slotIds.join(","), // 필요 없으면 빼도 됨
        tourSize,
      }),
    });
  };
  if (!date) {
    return (
      <div className="bg-white rounded-2xl p-8 text-center shadow-sm">
        <i className="ri-calendar-close-line text-4xl text-gray-300 mb-3"></i>
        <p className="text-sm text-gray-500">캘린더에서 원하시는 날짜를 클릭해주세요</p>
      </div>
    )
  }
  if (loading) {
    return (
      <div className="bg-white rounded-2xl p-8 text-center shadow-sm">
        <p className="text-sm text-gray-500">가능한 조합을 불러오는 중입니다...</p>
      </div>
    );
  }

  if (combinations.length === 0) {
    return (
      <div className="bg-white rounded-2xl p-8 text-center shadow-sm">
        <i className="ri-calendar-close-line text-4xl text-gray-300 mb-3"></i>
        <p className="text-sm text-gray-500">
          이 날짜에는 가능한 조합이 없습니다
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {combinations.map((combination, index) => (
        <ScheduleCombinationCard
          key={combination.id || index}
          combination={combination}
          booking={bookingIndex === index}
          onBook={() => handleBook(index)}
          handleMoveToBuilder={() => handleMoveToBuilder(index)}
        />
      ))}
    </div>
  );
}